/**
 * Data access for system messages — the notices Lotus staff target at users inside
 * accounts.
 *
 * Two audiences, one module:
 *	- the **reader** side (`activeMessages`, `loadActiveMessages`, `getMyMessageHistory`,
 *	  `dismissMessage`) drives the banner in the portal shell and the reader's Messages
 *	  page. Neither call sends an account or a user: the API answers for whoever the auth
 *	  token says is asking, so there is nothing here a caller could point at someone else.
 *	- the **Lotus staff** side (`getSystemMessages`, `saveSystemMessage`, …) drives the
 *	  console under /system-admin/system-messages. Those endpoints refuse anyone who
 *	  isn't Lotus staff; the front-end gating is convenience, not the control.
 *
 * `activeMessages` is a shared reactive ref rather than per-component state. The banner
 * lives in PrimaryLayout (mounted once for the whole signed-in session) while the Messages
 * page can dismiss a message — without a shared ref, closing one there would leave its
 * banner sitting in the shell until a full page reload.
 */

import { ref } from "vue";
import api2 from "/api2.js";




//---------------------------------------------------------------------------
// Reader side
//---------------------------------------------------------------------------

/**
 * The messages currently being shown to the signed-in user: live, addressed to them, and
 * not yet dismissed. The banner stack renders straight off this.
 */
export const activeMessages = ref([]);


// One load per signed-in session. The shell asks on mount; anything that changes the list
// (a dismissal) updates the ref in place rather than re-fetching.
let hasLoadedActiveMessages = false;




/**
 * Load the banner list.
 *
 * @param {boolean} options.force Re-fetch even when it has already been loaded — after a
 * sign-in, or when a page wants to be sure it is current.
 */
export async function loadActiveMessages(options = {})
{
	if (hasLoadedActiveMessages && options.force !== true) return activeMessages.value;

	// No overlay: this runs on every page load behind whatever the reader actually came
	// for, and must never put a spinner in front of it.
	const responseData = await api2.callApi1("GET", "/SystemMessages/GetMyMessages/v1", { IncludeHistory: false });
	if (responseData == null) return activeMessages.value;

	hasLoadedActiveMessages = true;

	activeMessages.value = (responseData.Messages || [])
		.filter((message) => message.IsCurrentlyActive && !message.IsDismissed);

	return activeMessages.value;
}




/**
 * Every message this reader has ever been shown, dismissed and expired ones included —
 * what the Messages page lists.
 */
export async function getMyMessageHistory()
{
	const responseData = await api2.callApi2WithOverlay("GET", "/SystemMessages/GetMyMessages/v1", { IncludeHistory: true });
	if (responseData == null) return [];

	return responseData.Messages || [];
}




/**
 * Close a message for the signed-in user, so it stops showing them a banner.
 *
 * Drops it from `activeMessages` on success so the banner goes immediately, wherever in
 * the app the dismissal came from.
 *
 * @returns {boolean} True when it was dismissed.
 */
export async function dismissMessage(systemMessageId)
{
	const request = { SystemMessageID: systemMessageId };

	const responseData = await api2.callApi1("POST", "/SystemMessages/SaveDismissMessage/v1", request);
	if (responseData == null) return false;

	activeMessages.value = activeMessages.value.filter((message) => message.ID != systemMessageId);

	return true;
}




/**
 * Forget the loaded banner list — on sign-out, so the next person to sign in on this
 * browser doesn't inherit the previous reader's banners before the first fetch lands.
 */
export function clearActiveMessagesCache()
{
	hasLoadedActiveMessages = false;
	activeMessages.value = [];
}




//---------------------------------------------------------------------------
// Lotus staff side
//---------------------------------------------------------------------------

/**
 * Every system message on the platform, for the staff console.
 *
 * @param {string} filters.StatusFilter A status code, or blank for every status.
 * @param {string} filters.TargetAccountIDFilter An account ID, or blank for every account.
 */
export async function getSystemMessages(filters = {})
{
	const request = {
		StatusFilter: filters.StatusFilter || "",
		TargetAccountIDFilter: filters.TargetAccountIDFilter || "",
	};

	const responseData = await api2.callApi2WithOverlay("GET", "/SystemMessages/GetSystemMessages/v1", request);
	if (responseData == null) return [];

	return responseData.Messages || [];
}




/**
 * One message by ID, for the staff editor. Null when it doesn't exist.
 */
export async function getSystemMessage(messageId)
{
	const responseData = await api2.callApi2WithOverlay("GET", "/SystemMessages/GetSystemMessage/v1", { ID: messageId });
	if (responseData == null) return null;

	return responseData.Message || null;
}




/**
 * Create or update a message. A new one is always created as a draft; this never changes
 * the status of an existing one — see `saveSystemMessageStatus`.
 *
 * @returns {string} The saved message's ID, or blank when the save failed.
 */
export async function saveSystemMessage(message)
{
	const request = {
		Message: {
			ID: message.ID || "",
			TargetAccountID: message.TargetAccountID || "",
			Title: message.Title,
			Body: message.Body || "",
			SeverityCode: message.SeverityCode,
			AudienceCode: message.AudienceCode,
			TargetUserIDs: message.TargetUserIDs || [],
			IsDismissible: message.IsDismissible === true,
			PublishFromUTC: message.PublishFromUTC,
			PublishUntilUTC: message.PublishUntilUTC || "",
			LinkUrl: message.LinkUrl || "",
			LinkText: message.LinkText || "",
		},
	};

	const responseData = await api2.callApi2WithOverlay("POST", "/SystemMessages/SaveSystemMessage/v1", request);
	if (responseData == null) return "";

	return responseData.ID || "";
}




/**
 * Publish a message, withdraw it, or return it to draft.
 *
 * @param {string} status A status code from `systemMessageStatuses`.
 * @returns {boolean} True when the status was changed.
 */
export async function saveSystemMessageStatus(messageId, status)
{
	const request = { ID: messageId, Status: status };

	const responseData = await api2.callApi2WithOverlay("POST", "/SystemMessages/SaveSystemMessageStatus/v1", request);

	return responseData != null;
}




/**
 * Every account on the platform, for the "which firm?" picker in the staff editor.
 * Lotus-only, and the same call the Accounts page makes.
 */
export async function getAllAccounts()
{
	const responseData = await api2.callApi1("GET", "/Accounts/GetAllAccounts", {});
	if (responseData == null) return [];

	return [...responseData.Accounts]
		.sort((account1, account2) => account1.Name.toLowerCase() > account2.Name.toLowerCase() ? 1 : -1);
}




/**
 * Everyone in one account, for naming individuals in the staff editor.
 *
 * Returns `Lotus.Models.AccountUser` records, whose identity property is **`UserID`** — not
 * `ID` — and which carry `FirstName`/`LastName`/`Email` but no `DisplayName`. Worth stating
 * because getting it wrong fails silently rather than loudly: an options list where every
 * `value` is `undefined` renders every checkbox as selected (they all compare equal), so the
 * symptom looks like a broken component rather than a bad property name.
 *
 * Sorted by first then last name, matching the API's own ordering and the account-admin
 * people list.
 */
export async function getUsersForAccount(accountId)
{
	if (!accountId) return [];

	const responseData = await api2.callApi1("GET", "/Users/GetAllAccountUsers/v2", { accountId: accountId });
	if (responseData == null) return [];

	return [...(responseData.AccountUsers || [])].sort(byFirstThenLastName);
}




function byFirstThenLastName(user1, user2)
{
	const first1 = (user1.FirstName || "").toLowerCase();
	const first2 = (user2.FirstName || "").toLowerCase();

	if (first1 > first2) return 1;
	if (first1 < first2) return -1;

	const last1 = (user1.LastName || "").toLowerCase();
	const last2 = (user2.LastName || "").toLowerCase();

	if (last1 > last2) return 1;
	if (last1 < last2) return -1;

	return 0;
}
